/**
 * PATH: apps/ai/src/pages/SettingsPage.tsx
 * PURPOSE: AI Hub settings with Outlook connection status
 * 
 * FEATURES:
 *   - Outlook connection status display
 *   - Connect/Disconnect Outlook flow
 *   - Token refresh capability
 *   - Connection status indicators
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  Settings, Mail, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Link2, Unlink, Loader2, ExternalLink,
  Clock, Shield, User, ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getAuthCookie, getOrgCookie } from '@quotech/auth';

import { API_BASE } from '@quotech/api';

// =============================================================================
// Types
// =============================================================================

interface OutlookStatus {
  connected: boolean;
  email?: string;
  display_name?: string;
  expires_at?: string;
  expires_in_seconds?: number;
  is_expired?: boolean;
  connected_at?: string;
  last_updated?: string;
  message?: string;
  authorize_url?: string;
}

type ConnectionState = 'idle' | 'loading' | 'connected' | 'disconnected' | 'expired' | 'error';

// =============================================================================
// API Functions
// =============================================================================

async function fetchOutlookStatus(): Promise<OutlookStatus> {
  const token = getAuthCookie();
  
  const response = await fetch(`${API_BASE}/api/outlook/status/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch status: ${response.status}`);
  }
  
  return response.json();
}

async function getAuthorizationUrl(): Promise<string> {
  const token = getAuthCookie();
  
  const response = await fetch(`${API_BASE}/api/outlook/authorize/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to get authorization URL: ${response.status}`);
  }
  
  const data = await response.json();
  return data.authorization_url;
}

async function disconnectOutlook(): Promise<void> {
  const token = getAuthCookie();
  
  const response = await fetch(`${API_BASE}/api/outlook/disconnect/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to disconnect: ${response.status}`);
  }
}

async function fetchGmailStatus(): Promise<{ connected: boolean; email: string | null; is_expired: boolean }> {
  const token = getAuthCookie();

  const response = await fetch(`${API_BASE}/api/gmail/status/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Gmail status: ${response.status}`);
  }

  return response.json();
}

async function getGmailAuthorizationUrl(): Promise<string> {
  const token = getAuthCookie();

  const response = await fetch(`${API_BASE}/api/gmail/authorize/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get Gmail authorization URL: ${response.status}`);
  }

  const data = await response.json();
  return data.authorization_url;
}

async function disconnectGmail(): Promise<void> {
  const token = getAuthCookie();

  const response = await fetch(`${API_BASE}/api/gmail/disconnect/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to disconnect Gmail: ${response.status}`);
  }
}

async function refreshOutlookToken(): Promise<void> {
  const token = getAuthCookie();
  
  const response = await fetch(`${API_BASE}/api/outlook/refresh/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to refresh token: ${response.status}`);
  }
}

// =============================================================================
// Outlook Connection Card
// =============================================================================

function OutlookConnectionCard() {
  const [status, setStatus] = useState<OutlookStatus | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  const loadStatus = useCallback(async () => {
    setConnectionState('loading');
    setError(null);
    
    try {
      const data = await fetchOutlookStatus();
      setStatus(data);
      
      if (data.connected) {
        if (data.is_expired) {
          setConnectionState('expired');
        } else {
          setConnectionState('connected');
        }
      } else {
        setConnectionState('disconnected');
      }
    } catch (err: unknown) {
      setError(err.message || 'Failed to load status');
      setConnectionState('error');
    }
  }, []);
  
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  
  const handleConnect = useCallback(async () => {
    setActionLoading(true);
    try {
      const authUrl = await getAuthorizationUrl();
      // Open in new window for OAuth flow
      window.location.href = authUrl;
    } catch (err: unknown) {
      setError(err.message || 'Failed to start connection');
    } finally {
      setActionLoading(false);
    }
  }, []);
  
  const handleDisconnect = useCallback(async () => {
    if (!confirm('Are you sure you want to disconnect Outlook?')) return;
    
    setActionLoading(true);
    try {
      await disconnectOutlook();
      await loadStatus();
    } catch (err: unknown) {
      setError(err.message || 'Failed to disconnect');
    } finally {
      setActionLoading(false);
    }
  }, [loadStatus]);
  
  const handleRefresh = useCallback(async () => {
    setActionLoading(true);
    try {
      await refreshOutlookToken();
      await loadStatus();
    } catch (err: unknown) {
      setError(err.message || 'Failed to refresh token');
    } finally {
      setActionLoading(false);
    }
  }, [loadStatus]);
  
  // Format time remaining
  const formatTimeRemaining = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    if (seconds < 60) return 'Less than a minute';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours`;
    return `${Math.floor(seconds / 86400)} days`;
  };
  
  // Status indicator config
  const statusConfig = {
    loading: {
      icon: Loader2,
      color: 'text-muted-foreground',
      bg: 'bg-muted',
      label: 'Checking...',
      animate: true,
    },
    connected: {
      icon: CheckCircle,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
      label: 'Connected',
      animate: false,
    },
    disconnected: {
      icon: XCircle,
      color: 'text-muted-foreground',
      bg: 'bg-muted',
      label: 'Not Connected',
      animate: false,
    },
    expired: {
      icon: AlertTriangle,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
      label: 'Token Expired',
      animate: false,
    },
    error: {
      icon: AlertTriangle,
      color: 'text-red-500',
      bg: 'bg-red-500/10',
      label: 'Error',
      animate: false,
    },
  }[connectionState];
  
  const StatusIcon = statusConfig.icon;
  
  return (
    <div className="p-6 rounded-xl border border-border bg-card">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/20">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Microsoft Outlook</h3>
            <p className="text-sm text-muted-foreground">Email integration for AI features</p>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium",
          statusConfig.bg, statusConfig.color
        )}>
          <StatusIcon className={cn("w-4 h-4", statusConfig.animate && "animate-spin")} />
          {statusConfig.label}
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-500 mb-4">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={loadStatus}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Connected State */}
      {connectionState === 'connected' && status && (
        <div className="space-y-4">
          {/* Connected Account Info */}
          <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-emerald-500/10">
                <User className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <p className="font-medium text-foreground">{status.display_name}</p>
                <p className="text-sm text-muted-foreground">{status.email}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>Expires in: {formatTimeRemaining(status.expires_in_seconds)}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Shield className="w-4 h-4" />
                <span>Secure connection</span>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-50"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh Token
            </button>
            
            <button
              onClick={handleDisconnect}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-500 text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              <Unlink className="w-4 h-4" />
              Disconnect
            </button>
          </div>
        </div>
      )}
      
      {/* Expired State */}
      {connectionState === 'expired' && status && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">
              Your Outlook connection has expired. Please refresh or reconnect.
            </p>
            <p className="text-xs text-muted-foreground">
              Connected as: {status.email}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh Token
            </button>
            
            <button
              onClick={handleConnect}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
            >
              <Link2 className="w-4 h-4" />
              Reconnect
            </button>
          </div>
        </div>
      )}
      
      {/* Disconnected State */}
      {connectionState === 'disconnected' && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50">
            <p className="text-sm text-foreground mb-3">
              Connect your Outlook account to enable:
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-primary" />
                Create email drafts directly from AI suggestions
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-primary" />
                Auto-attach QDrive documents to emails
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-primary" />
                AI-powered email composition
              </li>
            </ul>
          </div>
          
          <button
            onClick={handleConnect}
            disabled={actionLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 shadow-lg shadow-primary/20"
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4" />
            )}
            Connect Outlook
          </button>
        </div>
      )}
      
      {/* Loading State */}
      {connectionState === 'loading' && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}


// =============================================================================
// Gmail Connection Card
// =============================================================================

function GmailConnectionCard() {
  const [status, setStatus] = useState<{ connected: boolean; email: string | null; is_expired: boolean } | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    setConnectionState('loading');
    setError(null);

    try {
      const data = await fetchGmailStatus();
      setStatus(data);

      if (data.connected) {
        if (data.is_expired) {
          setConnectionState('expired');
        } else {
          setConnectionState('connected');
        }
      } else {
        setConnectionState('disconnected');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load status';
      setError(msg);
      setConnectionState('error');
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleConnect = useCallback(async () => {
    setActionLoading(true);
    try {
      const authUrl = await getGmailAuthorizationUrl();
      window.location.href = authUrl;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start Gmail connection';
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    if (!confirm('Are you sure you want to disconnect Gmail?')) return;

    setActionLoading(true);
    try {
      await disconnectGmail();
      await loadStatus();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to disconnect';
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  }, [loadStatus]);

  const statusConfig = {
    loading: {
      icon: Loader2, color: 'text-muted-foreground', bg: 'bg-muted',
      label: 'Checking...', animate: true,
    },
    connected: {
      icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10',
      label: 'Connected', animate: false,
    },
    disconnected: {
      icon: XCircle, color: 'text-muted-foreground', bg: 'bg-muted',
      label: 'Not Connected', animate: false,
    },
    expired: {
      icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-500/10',
      label: 'Token Expired', animate: false,
    },
    error: {
      icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10',
      label: 'Error', animate: false,
    },
  }[connectionState];

  const StatusIcon = statusConfig.icon;

  return (
    <div className="p-6 rounded-xl border border-border bg-card">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg shadow-red-500/20">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Gmail</h3>
            <p className="text-sm text-muted-foreground">Email integration via Google Workspace</p>
          </div>
        </div>

        <div className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium",
          statusConfig.bg, statusConfig.color
        )}>
          <StatusIcon className={cn("w-4 h-4", statusConfig.animate && "animate-spin")} />
          {statusConfig.label}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-500 mb-4">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={loadStatus} className="ml-auto text-xs underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {connectionState === 'connected' && status && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-full bg-emerald-500/10">
                <User className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <p className="font-medium text-foreground">{status.email || 'Connected'}</p>
                <p className="text-xs text-muted-foreground">{status.email}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleDisconnect}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-500 text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              <Unlink className="w-4 h-4" />
              Disconnect
            </button>
          </div>
        </div>
      )}

      {connectionState === 'expired' && status && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">
              Your Gmail connection has expired. Please reconnect.
            </p>
            <p className="text-xs text-muted-foreground">
              Connected as: {status.email}
            </p>
          </div>

          <button
            onClick={handleConnect}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
          >
            <Link2 className="w-4 h-4" />
            Reconnect
          </button>
        </div>
      )}

      {connectionState === 'disconnected' && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50">
            <p className="text-sm text-foreground mb-3">
              Connect your Gmail account to enable:
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-primary" />
                Send AI-drafted emails from your Gmail
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-primary" />
                Sync received messages into Quotech
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-primary" />
                AI-powered email composition
              </li>
            </ul>
          </div>

          <button
            onClick={handleConnect}
            disabled={actionLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#EA4335] text-white text-sm font-medium hover:bg-[#D33C2D] transition-colors disabled:opacity-50 shadow-lg shadow-red-500/20"
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4" />
            )}
            Connect Gmail
          </button>
        </div>
      )}

      {connectionState === 'loading' && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Settings Page
// =============================================================================

export default function SettingsPage() {
  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary/80">
              <Settings className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Settings</h1>
              <p className="text-muted-foreground">Configure AI Hub integrations</p>
            </div>
          </div>
        </div>
        
        {/* Integrations Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-muted-foreground" />
            Integrations
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OutlookConnectionCard />
            <GmailConnectionCard />
            
            {/* Placeholder for future integrations */}
            <div className="p-6 rounded-xl border border-dashed border-border bg-muted/20">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-muted">
                  <Link2 className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-muted-foreground">More Integrations</h3>
                  <p className="text-sm text-muted-foreground/70">Additional integrations coming soon</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
